import {
    Button,
    Card,
    CardActions,
    CardContent,
    colors,
    Grid,
    Modal,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { T, useT } from 'react-polyglot-hooks';
import validate from 'validate.js';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import anyLoading from '../../utils/anyLoading';
import { getHandleFieldChange, getHasError } from '../../utils/formHelpers';
import createEmail, { CreateEmailInput } from './createEmails.api';
import updateEmail, { UpdateEmailInput } from './updateEmails.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        boxShadow: theme.shadows[20],
        width: 700,
        maxHeight: '90%',
        overflowY: 'auto',
        maxWidth: '95%'
    },
    container: {
        marginTop: theme.spacing(3)
    },
    actions: {
        justifyContent: 'flex-end'
    },
    saveButton: {
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    }
}));

const EmailEdit = (props: {
    email?: any;
    onClose: () => void;
    open: boolean;
}) => {
    const { open, onClose, email, ...rest } = props;
    const classes = useStyles();
    const [sendCreateEmail, createEmailData] = createEmail();
    const [sendUpdateEmail, updateEmailData] = updateEmail();
    const loadingData = anyLoading(createEmailData, updateEmailData);
    const isNewEmail = !email.id;
    const { enqueueSnackbar } = useSnackbar();
    const t = useT();

    const newEmail = {
        email: ''
    };
    const [formState, setFormState] = useState(isNewEmail ? newEmail : email);
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            email: false
        }
    });

    const validateSchema = {
        email: {
            presence: { allowEmpty: false, message: t('is required') },
            email: true,
            length: {
                maximum: 64
            }
        }
    };

    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        setErrors(state => ({
            ...state,
            isValid: !newErrors,
            errors: newErrors || {}
        }));
    }, [formState]);

    useEffect(() => {
        setFormState(email);
    }, [email]);

    const onSaveClick = () => {
        if (isNewEmail) {
            const sanitisedData: CreateEmailInput = {
                data: {
                    email: formState.email
                }
            };

            sendCreateEmail({ variables: sanitisedData })
                .then(() => {
                    enqueueSnackbar(t('Email Created.'), {
                        variant: 'success'
                    });
                    onClose();
                })
                .catch(() => {
                    enqueueSnackbar(t('Email Creation Failed.'), {
                        variant: 'error'
                    });
                });
        } else {
            const sanitisedData: UpdateEmailInput = {
                data: {
                    id: formState.id,
                    email: formState.email
                }
            };

            sendUpdateEmail({ variables: sanitisedData })
                .then(() => {
                    enqueueSnackbar(t('Email Updated.'), {
                        variant: 'success'
                    });
                    onClose();
                })
                .catch(() => {
                    enqueueSnackbar(t('Email Update Failed.'), {
                        variant: 'error'
                    });
                });
        }
    };

    return (
        <Modal onClose={onClose} open={open}>
            <Card {...rest} className={clsx(classes.root)}>
                <LoadingOverlay loading={loadingData.loading}>
                    <form>
                        <CardContent>
                            <Typography
                                align="center"
                                gutterBottom
                                variant="h3"
                            >
                                {isNewEmail
                                    ? t('Create Email')
                                    : t('Edit Email')}
                            </Typography>
                            <Grid
                                className={classes.container}
                                container
                                spacing={3}
                            >
                                <Grid item md={6} xs={12}>
                                    <TextField
                                        {...hasError('email')}
                                        fullWidth
                                        label={t('Email address')}
                                        name="email"
                                        onChange={handleFieldChange}
                                        value={formState.email}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                        <CardActions className={classes.actions}>
                            <Button onClick={onClose} variant="contained">
                                <T phrase="Close" />
                            </Button>
                            <Button
                                className={classes.saveButton}
                                onClick={onSaveClick}
                                variant="contained"
                                disabled={!errors.isValid}
                            >
                                <T phrase="Save" />
                            </Button>
                        </CardActions>
                    </form>
                </LoadingOverlay>
            </Card>
        </Modal>
    );
};

export default EmailEdit;
